import React, { useEffect, useState } from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { apiClient } from '../../config/api';
import { AuthService } from '../../services/authService';

interface Documento {
  id: string;
  numeroDocumento: string;
  inicioVigencia?: string;
  fimVigencia?: string;
  premioLiquido?: number;
  comissao?: number;
}

interface DataItem {
  mesAno: string;
  produto: string;
  metaProduto?: { id: string; percentualParticipacao?: number; valorObjetivoVenda?: number };
  objetivoMensal?: { premioLiquido?: number; comissao?: number };
  totalAteHoje?: { premioLiquido?: number; comissao?: number; quantidadeDocumentos?: number };
  necessarioDiario?: { premioLiquido?: number; comissao?: number; diasRestantes?: number };
  realizadoInicioVigencia?: { totalPremioLiquido?: number; totalComissao?: number; quantidadeDocumentos?: number; documentos?: Documento[] };
  realizadoFimVigencia?: { totalPremioLiquido?: number; totalComissao?: number; quantidadeDocumentos?: number; documentos?: Documento[] };
  analise?: any;
}

interface ReportResponse {
  success: boolean;
  metaMensal?: any;
  data?: DataItem[];
}

export const MetaComissaoReport: React.FC = () => {
  const [data, setData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use centralized apiClient which already attaches auth token
      const endpoint = `/estatisticas/metas/produto-comissao/102025/comparacao`;
      const json = await apiClient.request<ReportResponse>(endpoint, { method: 'GET' });
      setData(json);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4 text-sm text-gray-600">Carregando relatório...</div>;
  if (error) return <div className="p-4 text-sm text-red-600">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded-md shadow-sm">
        <h3 className="text-sm font-semibold" style={{ color: '#3d2b56' }}>Relatório de Metas - {data.metaMensal?.mesAno}</h3>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-gray-500">Meta Prêmio</div>
            <div className="font-semibold">{formatCurrency(data.metaMensal?.valorPremio ?? 0)}</div>
          </div>
          <div>
            <div className="text-gray-500">Meta Comissão</div>
            <div className="font-semibold">{formatCurrency(data.metaMensal?.valorComissao ?? 0)}</div>
          </div>
          <div>
            <div className="text-gray-500">% Base</div>
            <div className="font-semibold">{data.metaMensal?.percentualBase ? `${(data.metaMensal.percentualBase * 100).toFixed(1)}%` : '-'}</div>
          </div>
          <div>
            <div className="text-gray-500">Realizado (Prêmio)</div>
            <div className="font-semibold">{formatCurrency(data.metaMensal?.realizado?.premioLiquido ?? 0)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {(data.data || []).map((item) => (
          <div key={`${item.produto}-${item.mesAno}`} className="bg-white rounded-md shadow-sm p-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-gray-500">Produto</div>
                <div className="font-semibold text-sm">{item.produto}</div>
                <div className="text-xs text-gray-500 mt-1">Objetivo Mensal: {formatCurrency(item.objetivoMensal?.premioLiquido ?? 0)} / {formatCurrency(item.objetivoMensal?.comissao ?? 0)}</div>
              </div>

              <div className="flex items-center gap-2">
                <button title="Detalhes" onClick={() => setExpanded((s) => ({ ...s, [item.produto]: !s[item.produto] }))} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded text-xs hover:bg-gray-100">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  <span>{expanded[item.produto] ? 'Ocultar' : 'Detalhes'}</span>
                </button>

                <button title="Exportar CSV" onClick={() => { /* placeholder export */ }} className="flex items-center gap-2 px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:opacity-90">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m7-7H5"/></svg>
                  <span>Exportar</span>
                </button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-gray-500">Total até hoje</div>
                <div className="font-semibold">{formatCurrency(item.totalAteHoje?.premioLiquido ?? 0)} / {formatCurrency(item.totalAteHoje?.comissao ?? 0)}</div>
                <div className="text-gray-500">Docs: {item.totalAteHoje?.quantidadeDocumentos ?? 0}</div>
              </div>

              <div>
                <div className="text-gray-500">Necessário diário</div>
                <div className="font-semibold">{formatCurrency(item.necessarioDiario?.premioLiquido ?? 0)} / {formatCurrency(item.necessarioDiario?.comissao ?? 0)}</div>
                <div className="text-gray-500">Dias restantes: {item.necessarioDiario?.diasRestantes ?? '-'}</div>
              </div>

              <div>
                <div className="text-gray-500">Realizado (Início Vigência)</div>
                <div className="font-semibold">{formatCurrency(item.realizadoInicioVigencia?.totalPremioLiquido ?? 0)} / {formatCurrency(item.realizadoInicioVigencia?.totalComissao ?? 0)}</div>
                <div className="text-gray-500">Docs: {item.realizadoInicioVigencia?.quantidadeDocumentos ?? 0}</div>
              </div>
            </div>

            {expanded[item.produto] && (
              <div className="mt-3 border-t pt-2 text-xs">
                <div className="font-semibold mb-1">Documentos (Início Vigência)</div>
                {(item.realizadoInicioVigencia?.documentos || []).map((d) => (
                  <div key={d.id} className="flex items-center justify-between py-1">
                    <div>
                      <div className="text-sm">#{d.numeroDocumento} — {d.premioLiquido ? formatCurrency(d.premioLiquido) : '-'}</div>
                      <div className="text-gray-500 text-xs">Início: {d.inicioVigencia ? formatDate(d.inicioVigencia) : '-'}</div>
                    </div>
                    <div className="text-xs text-gray-500">Comissão: {d.comissao ? formatCurrency(d.comissao) : '-'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetaComissaoReport;
